<div class="site-container">
  <div class="card">
    @if (loading()) {
      <div class="grid mb-7">
        <div class="col-12 lg:col-8 product-pictures" style="border-radius: 1.5rem">
          <p-skeleton width="100%" height="50vh" [style]="{ borderRadius: '1.5rem' }"></p-skeleton>
        </div>
        <div class="col-12 lg:col-4" style="border-radius: 1.5rem">
          <p-skeleton width="100%" height="50vh" [style]="{ borderRadius: '1.5rem' }"></p-skeleton>
        </div>
      </div>
      <!-- ============================================================================================== -->
    } @else {
      <!-- ====================== CONTENT ====================== -->
      <div class="grid mb-7">
        <!-- Left Column: Images -->
        <div class="col-12 lg:col-8 product-pictures">
          @if (isMobile) {
            <div class="full-width-wrapper">
              <div
                class="mobile-images"
                (touchstart)="onTouchStart($event)"
                (touchend)="onTouchEnd($event)"
              >
                <p-galleria
                  [value]="galleriaImages"
                  [showItemNavigators]="false"
                  [showThumbnails]="false"
                  [showIndicators]="true"
                  [showIndicatorsOnItem]="true"
                  [circular]="true"
                  [(activeIndex)]="galleriaActiveIndex"
                  [containerStyle]="{
                    'max-width': '576px',
                    'max-height': '25rem',
                    'margin-top': '2rem'
                  }"
                >
                  <ng-template #item>
                    <img
                      [src]="galleriaImages[galleriaActiveIndex]?.itemImageSrc"
                      style="width: 100%; display: block; user-select: none"
                      [alt]="galleriaImages[galleriaActiveIndex]?.alt"
                    />
                  </ng-template>
                </p-galleria>
              </div>
            </div>
          } @else {
            <div class="flex justify-content-between">
              <!-- Thumbnails -->
              @if (!selectedProduct.is_gift_card) {
                <div class="flex flex-column w-2 mr-3" style="row-gap: 1rem">
                  @for (image of images; track i; let i = $index) {
                    @if (image) {
                      <img
                        tabindex="0"
                        role="button"
                        [src]="image ?? 'assets/demo/images/product/t-shirt-placeholder.png'"
                        class="w-full border-2 border-transparent border-round cursor-pointer side-image"
                        alt="product-img"
                        (click)="selectImageIndex(i)"
                        (keydown.enter)="selectImageIndex(i)"
                      />
                    }
                  }
                </div>
              }

              <!-- Main Image -->
              <div
                class="main-img flex block-shadow p-5"
                [ngClass]="selectedProduct.is_gift_card ? 'w-12' : 'w-10'"
              >
                <p-image
                  [src]="
                    images[selectedImageIndex] ||
                    (selectedProduct.is_gift_card
                      ? 'assets/images/giftcard.png'
                      : selectedProduct.is_custom
                        ? 'assets/images/custom-product.png'
                        : 'assets/demo/images/product/t-shirt-placeholder.png')
                  "
                  class="w-full border-2 border-transparent border-round product-img-width flex justify-content-center align-self-center"
                  alt="product-img"
                  preview
                ></p-image>
              </div>
            </div>
          }
        </div>
        <!-- Right Column: Product Details -->
        <div class="col-12 lg:col-4 p-5 mt-3 block-shadow">
          <!-- Product Name -->
          @if (selectedProduct.ribbon) {
            <div class="product-page-ribbon">
              {{ selectedProduct.ribbon }}
            </div>
          }
          <div class="flex align-items-center text-900 mb-2 product-name-font">
            <p>{{ selectedProduct.name }}</p>
          </div>
          <small
            class="ql-editor block product-desc"
            style="height: auto; padding: 0"
            [innerHTML]="selectedProduct.short_description | normalizeHtml"
          ></small>
          <!-- Rating -->
          <div class="flex align-items-center justify-content-between mb-2">
            <div class="flex align-items-center">
              @if (selectedProduct.reviews_rate_average) {
                <span class="mr-3">
                  @for (star of [1, 2, 3, 4, 5]; track star) {
                    <i
                      class="pi"
                      [ngClass]="
                        star <= selectedProduct.reviews_rate_average
                          ? 'pi-star-fill text-yellow-500'
                          : 'pi-star text-600'
                      "
                      aria-hidden="true"
                    ></i>
                  }
                </span>
              }
              @if (selectedProduct.reviews_count) {
                <span class="text-sm">
                  <b class="text-900 mr-1">{{ selectedProduct.reviews_count }}</b>
                  {{ selectedProduct.reviews_count > 1 ? 'reviews' : 'review' }}
                </span>
              }
            </div>
          </div>

          <!-- Decoration Method -->
          @if (selectedProduct.decoration_methods?.length) {
            <div class="flex align-items-center mb-3">
              <img src="assets/img/icons/product/magicpen.svg" alt="color-icon" />
              <span class="ml-2">Decoration Method</span>
              <div class="decoration-methods-chips ml-2">
                @for (method of selectedProduct.decoration_methods; track method.id) {
                  <p-chip class="decoration-chip" [label]="method.name"></p-chip>
                }
              </div>
            </div>
          }

          <!-- Color Title -->
          @if (!selectedProduct.is_gift_card) {
            <div class="flex align-items-center">
              <img src="assets/img/icons/product/color.svg" alt="color-icon" />
              <span class="text-900 ml-2">
                {{ selectedVariant()?.color ? 'Color: ' + selectedVariant()?.color : 'Color' }}
              </span>
            </div>
            <!-- Color Buttons -->
            <div class="flex align-items-center">
              <div class="flex align-items-center mb-3">
                <div class="mt-2 flex flex-wrap gap-2 p-2">
                  @for (variant of selectedProduct.product_variants; track variant.id) {
                    @if (variant.primary_hex_color || variant.swatch_url) {
                      <!-- Render a colored circle wrapped in a container -->
                      <div
                        tabindex="0"
                        role="button"
                        class="color-option"
                        [ngClass]="{ selected: selectedVariant()?.id === variant.id }"
                        (click)="selectVariant(variant)"
                        (keydown.enter)="selectVariant(variant)"
                        title="{{ variant.color }}"
                      >
                        <div
                          class="color-circle"
                          [ngStyle]="{
                            'background-color': variant.primary_hex_color,
                            border: isLightColor(variant.primary_hex_color)
                              ? '1px solid #ccc'
                              : 'none'
                          }"
                        >
                          @if (variant.swatch_url) {
                            <img
                              style="border-radius: 50%"
                              [src]="variant.swatch_url"
                              [alt]="variant.color"
                            />
                          }
                        </div>
                      </div>
                    } @else {
                      <!-- Fall back to the button with color name if no primary_hex_color is provided -->
                      <app-button
                        [label]="variant.color"
                        [buttonStyle]="
                          selectedVariant()?.id !== variant.id ? 'outlined' : 'primary'
                        "
                        (click)="selectVariant(variant)"
                        class="variantButton"
                      ></app-button>
                    }
                  }
                </div>
              </div>
            </div>
          }

          <!-- Size Title -->
          <div class="flex align-items-center justify-content-between mb-2">
            @if (!selectedProduct.is_gift_card) {
              <div class="flex align-items-center">
                <img src="assets/img/icons/product/size.svg" alt="size-icon" />
                <span class="text-900 ml-2">Size</span>
              </div>
            } @else {
              <div class="flex align-items-center">
                <img src="assets/img/icons/product/empty-wallet.svg" alt="size-icon" />
                <span class="text-900 ml-2">Amount</span>
              </div>
            }
            @if (
              !selectedProduct.is_custom &&
              !selectedProduct.is_gift_card &&
              !selectedProduct.hide_size_guide
            ) {
              <a
                tabindex="0"
                role="button"
                class="cursor-pointer text-600 text-sm flex align-items-center"
                (click)="sizeGuideDialog.open()"
                (keydown.enter)="sizeGuideDialog.open()"
              >
                Size Guide
                <i class="ml-1 pi pi-angle-right"></i>
              </a>
            }
          </div>
          <!-- Sizes -->
          <div class="grid grid-nogutter align-items-center mb-1">
            @if (availableSizes.length > 0) {
              @if (selectedVariant() && !selectedVariant()?.product_variant_sizes?.length) {
                <p>
                  No {{ selectedProduct.is_gift_card ? 'amounts' : 'sizes' }} available for the
                  selected variant.
                </p>
              }
              @if (!selectedVariant() || selectedVariant()?.product_variant_sizes?.length) {
                @for (sizeOption of availableSizes; track sizeOption) {
                  <div class="flex align-items-center">
                    <div class="flex flex-wrap gap-2 p-2">
                      <app-button
                        [label]="
                          selectedProduct.is_gift_card
                            ? ((sizeOption | usdPipe: 'USD' : 'symbol' : '1.0-2') ?? '')
                            : sizeOption
                        "
                        [buttonStyle]="size() !== sizeOption ? 'outlined' : 'primary'"
                        (click)="selectSize(sizeOption)"
                        [disabled]="isSizeDisabled(sizeOption)"
                        class="sizeButton"
                        [class.disabledButton]="isSizeDisabled(sizeOption)"
                      ></app-button>
                    </div>
                  </div>
                }
              }
            } @else {
              <p>No sizes available for this product.</p>
            }
          </div>

          @if (selectedProduct.show_inventory_levels && selectedVariant() && size()) {
            <div
              class="stock-indicator"
              [ngClass]="selectedVariantQuantity()! <= 5 ? 'stock-low' : 'stock-ok'"
            >
              <span class="stock-dot"></span>
              @if (selectedVariantQuantity() === 0) {
                Out of stock in selected size
              } @else {
                {{ selectedVariantQuantity() }} left in selected size
              }
            </div>
          }

          @if (selectedProduct.is_gift_card) {
            <div class="mb-3">
              <div class="flex align-items-center">
                <p-checkbox
                  [binary]="true"
                  [formControl]="useCustomAmount"
                  (onChange)="onCustomAmountToggle()"
                  inputId="useCustomAmount"
                ></p-checkbox>

                <label class="custom-amount-label ml-2" for="useCustomAmount">
                  Enter your own amount
                </label>
              </div>
            </div>
          }
          @if (useCustomAmount.value) {
            <div class="mb-3">
              <app-input
                type="number"
                [control]="giftCardAmount"
                label="Gift card amount"
                placeholder=""
              ></app-input>
            </div>
          }

          @if (selectedProduct.is_gift_card) {
            <div class="mb-4">
              <app-input
                id="recipientName"
                [control]="giftCardRecipientName"
                type="text"
                label="{{ 'PRODUCT.recipientName' | translate }}"
                name="RecipientName"
              ></app-input>
            </div>
            <div class="mb-4">
              <app-input
                id="email"
                [control]="giftCardEmail"
                type="email"
                label="{{ 'PRODUCT.recipientEmailAddress' | translate }}"
                name="Email"
              ></app-input>
              <small style="display: block; line-height: 17px; font-size: 10px">
                {{ 'PRODUCT.recipientEmailInfo' | translate }}
              </small>
            </div>

            <div class="mb-3">
              <label for="giftCardNote" class="personal-message-label">
                {{ 'PRODUCT.personalMessage' | translate }}
              </label>
              <textarea
                id="giftCardNote"
                [formControl]="giftCardNote"
                pTextarea
                rows="3"
                maxlength="100"
                placeholder="Write a personal message for the gift card recipient..."
                class="w-full note"
              ></textarea>
              <div class="flex justify-content-between align-items-center mt-1">
                <div class="flex align-items-center">
                  <img src="assets/img/icons/product/heart.svg" alt="color-icon" />
                  <label for="note" class="custom-amount-label ml-2">
                    Add a personal touch to your gift card
                  </label>
                </div>
                <small class="text-600">{{ giftCardNote.value?.length || 0 }}/100</small>
              </div>
            </div>
          }

          <!-- Price -->
          @if (!disablePrices && !cartStore.hideProductPrices()) {
            <div class="flex align-items-center">
              <img src="assets/img/icons/product/dollar-circle.svg" alt="size-icon" />
              <span class="text-900 ml-2">Price</span>
            </div>
            <div class="product-page-price mt-2">
              @if (
                selectedProduct.original_display_price && selectedProduct.original_display_price > 0
              ) {
                <span class="product-page-original-price">
                  {{ selectedProduct.original_display_price! | price | usdPipe }}
                </span>
              }
              @if (
                selectedProduct.show_tier_prices_only &&
                selectedProduct.lowest_tier_prices_data?.price &&
                !pricingSelectionChanged()
              ) {
                <span class="product-page-current-price">
                  {{ selectedProduct.lowest_tier_prices_data!.price | price | usdPipe }}
                </span>
              } @else {
                <span class="product-page-current-price">
                  {{ displayPrice | price | usdPipe }}
                </span>
              }
            </div>

            @if (bulkDiscountSummary) {
              <div
                class="volume-discount-card"
                tabindex="0"
                role="button"
                (click)="openBulkPricingDialog()"
                (keydown.enter)="openBulkPricingDialog()"
              >
                <div class="volume-discount-icon">
                  <i class="pi pi-percentage"></i>
                </div>
                <div class="volume-discount-text">
                  <p class="volume-discount-title">Volume discounts</p>
                  <p class="volume-discount-subtitle">
                    Save up to {{ bulkDiscountSummary.maxSavingsPercent }}% when you order
                    {{ bulkDiscountSummary.minQuantityForMax }} or more.
                  </p>
                </div>
                <div class="volume-discount-cta">
                  View tiers
                  <i class="pi pi-angle-right"></i>
                </div>
              </div>
            }
          }

          @if (customInputsForm && selectedProduct.input_fields?.length) {
            <form [formGroup]="customInputsForm">
              <div formArrayName="inputFields">
                @for (field of selectedProduct.input_fields; track field.id; let i = $index) {
                  <div [formGroupName]="i" class="mb-3">
                    @switch (inputFields.controls[i].get('type')?.value) {
                      @case ('dropdown') {
                        <app-select
                          [options]="getControl(i, 'dropdownOptions').value"
                          [control]="getControl(i, 'value')"
                          [placeholder]="getControl(i, 'placeholder').value || 'Select an option'"
                          [label]="getControl(i, 'label').value"
                        ></app-select>
                      }
                      @case ('toggle') {
                        <div class="flex flex-column mb-2">
                          <label for="custom-input-{{ field.id }}" class="personal-message-label">
                            {{ getControl(i, 'label').value }}
                          </label>
                          <span
                            style="width: fit-content"
                            [pTooltip]="
                              getControl(i, 'value').value
                                ? getControl(i, 'onLabel').value
                                : getControl(i, 'offLabel').value
                            "
                            tooltipPosition="bottom"
                          >
                            <p-toggleswitch [formControl]="getControl(i, 'value')"></p-toggleswitch>
                          </span>
                        </div>
                      }
                      @default {
                        <app-input
                          id="custom-input-{{ field.id }}"
                          [control]="inputFields.controls[i].get('value')!"
                          type="text"
                          [placeholder]="inputFields.controls[i].get('placeholder')?.value"
                          [label]="inputFields.controls[i].get('label')?.value"
                          [name]="inputFields.controls[i].get('label')?.value"
                        ></app-input>
                      }
                    }
                  </div>
                }
              </div>
            </form>
          }

          @if (messages.length) {
            <div class="mt-2">
              @for (message of messages; track i; let i = $index) {
                <p-message [severity]="message.severity">{{ message.detail }}</p-message>
              }
            </div>
          }
          <!-- Quantity & Add to Cart -->
          <div
            class="flex flex-row align-items-center justify-content-between mt-4 mb-4 quantity-input"
          >
            <p-inputNumber
              [showButtons]="true"
              buttonLayout="horizontal"
              spinnerMode="horizontal"
              [min]="minQuantity"
              inputStyleClass="quantity-input text-center"
              [(ngModel)]="quantity"
              (ngModelChange)="onQuantityChange($event)"
              decrementButtonClass="p-button-text"
              incrementButtonClass="p-button-text"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
            ></p-inputNumber>

            <div class="flex align-items-center flex-1 ml-3">
              <app-button
                [label]="
                  popupPurchaseBlocked
                    ? 'Ordering closed'
                    : outOfStock()
                      ? giftQuantityLimitReached()
                        ? 'Max. Qty Allowed'
                        : 'Out of Stock'
                      : belowMinimumOrderQuantity()
                        ? 'Min. Qty ' + selectedProduct.minimum_order_quantity_amount
                        : 'Add to Cart'
                "
                class="flex-1 add-to-cart"
                [disabled]="
                  popupPurchaseBlocked ||
                  (storefrontStatus === 'live' &&
                    (!selectedProduct.library_approved || selectedProduct.need_attention !== 0)) ||
                  outOfStock() ||
                  belowMinimumOrderQuantity()
                "
                [fullWidth]="true"
                (handleClick)="addItem()"
              ></app-button>
            </div>
          </div>
          @if (
            selectedProduct.minimum_order_quantity && selectedProduct.minimum_order_quantity_amount
          ) {
            <small class="minimum-order-hint block mb-3">
              Minimum order quantity: {{ selectedProduct.minimum_order_quantity_amount }}
            </small>
          }
          <app-product-note [showNoteForCustomers]="!selectedProduct.is_custom" />
          @if (selectedProduct.copy_underneath_description) {
            <small
              class="ql-editor block mt-2 product-desc"
              style="height: auto; padding: 0"
              [innerHTML]="selectedProduct.copy_underneath_description | normalizeHtml"
            ></small>
          }
          @if (selectedProduct.description) {
            <div class="mt-5">
              <p class="text-900 mb-2 font-semibold">Product Details</p>
              @if (isArrayDescription) {
                <ul>
                  @for (item of displayedDescription; track $index) {
                    <li class="font-light">
                      {{ item }}
                    </li>
                  }
                </ul>
                <div class="flex justify-content-end">
                  @if (shouldShowToggleButton()) {
                    <button (click)="toggleDescription()" class="description-toggle-btn">
                      {{ isDescriptionExpanded ? '< Less' : 'More >' }}
                    </button>
                  }
                </div>
              } @else {
                <div
                  class="ql-editor custom-height"
                  [ngClass]="{ 'full-height-content': isDescriptionExpanded }"
                  [innerHTML]="displayedDescription"
                ></div>
                <div class="flex justify-content-end">
                  @if (shouldShowToggleButton()) {
                    <button (click)="toggleDescription()" class="description-toggle-btn">
                      {{ isDescriptionExpanded ? '< Less' : 'More >' }}
                    </button>
                  }
                </div>
              }
            </div>
          }
        </div>
      </div>
      <!-- ====================================================================== -->
    }
  </div>
</div>

<p-dialog
  header="Item added to cart"
  [(visible)]="displayDialog"
  [closable]="true"
  [style]="{ width: '400px' }"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
>
  <ng-template #footer>
    <div class="flex justify-content-between w-full">
      <div>
        <app-button
          type="button"
          label="Continue Shopping"
          buttonStyle="outlined"
          (handleClick)="continueShopping()"
        ></app-button>
      </div>
      <div>
        <app-button
          type="button"
          label="Go To Checkout"
          (handleClick)="goToCheckout()"
        ></app-button>
      </div>
    </div>
  </ng-template>
</p-dialog>

@if (selectedProduct) {
  <app-size-guide-dialog
    #sizeGuideDialog
    [sizeGuides]="selectedProduct.product_size_guides"
  ></app-size-guide-dialog>
}

@if (selectedProduct && isBulkPricingEnabled) {
  <app-bulk-pricing-dialog
    #bulkPricingDialog
    [sizes]="selectedVariant()?.product_variant_sizes ?? []"
  ></app-bulk-pricing-dialog>
}
